// src/Pages/Cart.js
// ✅ BEFORE LOGIN: Shows guest cart items (from localStorage) — NO payment section
// ✅ "Login to Checkout" banner — clicking navigates to /login (saves returnTo)
// ✅ AFTER LOGIN: Merges guest cart into API cart, then shows full cart + payment
// ✅ Complete redesign — clean white/slate aesthetic with warm gold accents

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

// ─────────────────────────────────────────────
//  GUEST CART HELPERS (same as Products.js)
// ─────────────────────────────────────────────
const GUEST_CART_KEY = "guest_cart";

const guestCart = {
  get() {
    try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "{}"); }
    catch { return {}; }
  },
  set(map) {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(map));
    window.dispatchEvent(new Event("guestCartUpdated"));
  },
  updateQty(productId, newQty) {
    const cart = guestCart.get();
    const pid = String(productId);
    if (newQty < 1) delete cart[pid];
    else {
      if (!cart[pid]) cart[pid] = { qty: newQty, product: null };
      else cart[pid].qty = newQty;
    }
    guestCart.set(cart);
    return cart;
  },
  remove(productId) {
    const cart = guestCart.get();
    delete cart[String(productId)];
    guestCart.set(cart);
    return cart;
  },
  clear() {
    localStorage.removeItem(GUEST_CART_KEY);
    window.dispatchEvent(new Event("guestCartUpdated"));
  },
  toArray(cart) {
    return Object.entries(cart).map(([pid, row]) => ({
      productId: pid,
      qty: row.qty,
      product: row.product || {},
    }));
  },
  totalCount(cart) {
    return Object.values(cart).reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
  },
};

// ─────────────────────────────────────────────
//  IMAGE UTILS
// ─────────────────────────────────────────────
const normalizeImages = (images) => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  if (typeof images === "string") {
    const s = images.trim();
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") return [parsed];
    } catch {
      const cleaned = s.replace(/^"+|"+$/g, "");
      return cleaned ? [cleaned] : [];
    }
  }
  return [];
};

// ─────────────────────────────────────────────
//  MAIN CART COMPONENT
// ─────────────────────────────────────────────
export default function Cart() {
  const navigate = useNavigate();
  const topRef = useRef(null);
  const isLoggedIn = Boolean(localStorage.getItem("token"));

  // ── STATE ──
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [merging, setMerging] = useState(false);

  // Guest cart state (for non-logged-in)
  const [guestItems, setGuestItems] = useState([]);

  // Redirect guest to register if they try to access Cart directly
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/register");
    }
  }, [isLoggedIn, navigate]);

  // User info
  const [me, setMe] = useState(null);

  // Address state
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrErr, setAddrErr] = useState("");
  const [addrMsg, setAddrMsg] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [addrAlertOpen, setAddrAlertOpen] = useState(false);

  // Wallet
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletErr, setWalletErr] = useState("");

  // Payment
  const [paymentMethod, setPaymentMethod] = useState("RAZORPAY");
  const [paying, setPaying] = useState(false);
  const [rzpReady, setRzpReady] = useState(false);

  // Delivery
  const [deliveryRules, setDeliveryRules] = useState([]);
  const [dcLoading, setDcLoading] = useState(false);
  const [dcErr, setDcErr] = useState("");

  const emptyForm = {
    label: "Home", pincode: "", house: "", area: "", state: "",
    street: "", village: "", mandal: "", panchayati: "", district: "",
    receiverFirstName: "", receiverLastName: "", receiverPhone: "", isDefault: true,
  };
  const [form, setForm] = useState(emptyForm);

  // ── API ORIGIN ──
  const API_ORIGIN = useMemo(() => {
    const base = (axiosInstance.defaults.baseURL || "").trim();
    if (!base) return "http://localhost:3000";
    return base.replace(/\/+$/, "");
  }, []);

  const imgUrl = useCallback((path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${API_ORIGIN}${p}`;
    return `${API_ORIGIN}/${p}`;
  }, [API_ORIGIN]);

  const safeNum = (v) => { const n = Number(v ?? 0); return Number.isFinite(n) ? n : 0; };
  const money = (v) => safeNum(v).toFixed(2);
  const pctText = (n) => { const x = safeNum(n); if (x <= 0) return "0%"; return `${x.toFixed(2).replace(/\.00$/, "")}%`; };

  // ── CART HELPERS ──
  const cartItems = useMemo(() => (data?.CartItems || data?.cartItems || data?.items || []) ?? [], [data]);
  const getProduct = (it) => it?.product || it?.Product || it?.productDetails || {};
  const getQty = (it) => safeNum(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1) || 1;
  const itemsCount = data?.itemsCount ?? cartItems.length ?? 0;
  const totalQty = data?.totalQty ?? cartItems.reduce((s, it) => s + getQty(it), 0);

  // ── USER TYPE ──
  const userType = String(me?.userType || "").toUpperCase().trim();
  const getApplicablePercent = (p) => {
    if (userType === "ENTREPRENEUR") return safeNum(p?.entrepreneurDiscount ?? 0);
    if (userType === "TRAINEE_ENTREPRENEUR") return safeNum(p?.traineeEntrepreneurDiscount ?? 0);
    return 0;
  };

  const subtotal = data?.totalAmount ?? cartItems.reduce((s, it) => {
    const p = getProduct(it);
    return s + safeNum(p?.price ?? it?.price ?? 0) * getQty(it);
  }, 0);

  const summaryProducts = useMemo(() => cartItems.map((it) => {
    const p = getProduct(it);
    const qty = getQty(it);
    const price = safeNum(p?.price ?? it?.price ?? 0);
    const percent = getApplicablePercent(p);
    const lineTotal = price * qty;
    const lineDiscount = (lineTotal * percent) / 100;
    return {
      id: it?.id ?? it?._id ?? `${p?.id || ""}-${p?.name || ""}`,
      name: p?.name || it?.name || "Product",
      qty, price, percent, lineTotal, lineDiscount,
    };
  }), [cartItems, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  const discountAmount = useMemo(() =>
    Math.min(summaryProducts.reduce((s, x) => s + safeNum(x.lineDiscount), 0), safeNum(subtotal)),
    [summaryProducts, subtotal]);

  const subtotalAfterDiscount = useMemo(() => Math.max(0, safeNum(subtotal) - safeNum(discountAmount)), [subtotal, discountAmount]);

  const deliveryCharge = useMemo(() => {
    const base = safeNum(subtotalAfterDiscount);
    if (!Array.isArray(deliveryRules) || deliveryRules.length === 0) return 0;
    const active = deliveryRules.filter((r) => r?.isActive !== false);
    const matches = active.filter((r) => {
      const min = safeNum(r?.minAmount ?? 0);
      const max = safeNum(r?.maxAmount ?? 0);
      return base >= min && base <= max;
    });
    if (matches.length === 0) return 0;
    matches.sort((a, b) => safeNum(a.maxAmount) - safeNum(b.maxAmount));
    return safeNum(matches[0]?.charge ?? 0);
  }, [deliveryRules, subtotalAfterDiscount]);

  const grandTotal = useMemo(() => safeNum(subtotalAfterDiscount) + safeNum(deliveryCharge), [subtotalAfterDiscount, deliveryCharge]);

  // ─────────────────────────────────────────────
  //  LOADERS
  // ─────────────────────────────────────────────
  // ── Helper: fire navbar badge event ──
  const fireCartCount = (cartData) => {
    const items = cartData?.CartItems || cartData?.cartItems || cartData?.items || [];
    const total = Array.isArray(items)
      ? items.reduce((sum, it) => sum + (Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1) || 0), 0)
      : 0;
    window.dispatchEvent(new CustomEvent("cartCountUpdated", { detail: { count: total } }));
  };

  const loadCart = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      setErr("");
      const res = await axiosInstance.get("/api/cart", { params: { _ts: Date.now() } });
      setData(res.data);
      fireCartCount(res.data);
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load cart");
      setData(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMe = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/users/me");
      setMe(res?.data?.user || res?.data || null);
    } catch { setMe(null); }
  }, []);

  const loadAddresses = useCallback(async () => {
    try {
      setAddrLoading(true);
      setAddrErr("");
      const res = await axiosInstance.get("/api/addresses");
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.addresses) ? raw.addresses : [];
      setAddresses(list);
    } catch (e) {
      setAddrErr(e?.response?.data?.msg || e?.message || "Failed to load addresses");
      setAddresses([]);
    } finally { setAddrLoading(false); }
  }, []);

  const loadWallet = useCallback(async () => {
    try {
      setWalletLoading(true);
      setWalletErr("");
      const res = await axiosInstance.get("/api/wallet");
      setWallet(res.data || null);
    } catch (e) {
      setWalletErr(e?.response?.data?.msg || e?.message || "Failed to load wallet");
      setWallet(null);
    } finally { setWalletLoading(false); }
  }, []);

  const loadDeliveryCharges = useCallback(async () => {
    try {
      setDcLoading(true);
      setDcErr("");
      const res = await axiosInstance.get("/api/deliverycharges");
      setDeliveryRules(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setDcErr(e?.response?.data?.msg || e?.message || "");
      setDeliveryRules([]);
    } finally { setDcLoading(false); }
  }, []);

  // ─────────────────────────────────────────────
  //  MERGE GUEST CART → API ON LOGIN
  // ─────────────────────────────────────────────
  const mergeGuestCartToAPI = useCallback(async () => {
    const raw = guestCart.get();
    const items = guestCart.toArray(raw);
    if (items.length === 0) return;

    setMerging(true);
    try {
      for (const item of items) {
        try {
          await axiosInstance.post("/api/cart", {
            productId: Number(item.productId),
            qty: item.qty,
          });
        } catch { /* skip if individual item fails */ }
      }
      guestCart.clear();
    } catch { /* ignore */ } finally {
      setMerging(false);
    }
  }, []);

  // ─────────────────────────────────────────────
  //  RAZORPAY SCRIPT
  // ─────────────────────────────────────────────
  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const existing = document.getElementById("razorpay-checkout-js");
      if (existing) {
        existing.addEventListener("load", () => resolve(true));
        existing.addEventListener("error", () => resolve(false));
        return;
      }
      const script = document.createElement("script");
      script.id = "razorpay-checkout-js";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  // ─────────────────────────────────────────────
  //  INITIAL LOAD
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) {
      // Guest mode — load guest cart from localStorage
      const raw = guestCart.get();
      setGuestItems(guestCart.toArray(raw));
      setLoading(false);
      return;
    }

    // Logged in — merge guest cart first, then load everything
    (async () => {
      await mergeGuestCartToAPI();
      await Promise.all([loadCart(), loadMe(), loadAddresses(), loadWallet(), loadDeliveryCharges()]);
      const ok = await loadRazorpayScript();
      setRzpReady(!!ok);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for guest cart changes
  useEffect(() => {
    if (isLoggedIn) return;
    const onUpdate = () => {
      const raw = guestCart.get();
      setGuestItems(guestCart.toArray(raw));
    };
    window.addEventListener("guestCartUpdated", onUpdate);
    return () => window.removeEventListener("guestCartUpdated", onUpdate);
  }, [isLoggedIn]);

  // ─────────────────────────────────────────────
  //  DEFAULT ADDRESS AUTO-SELECT
  // ─────────────────────────────────────────────
  const defaultAddress = useMemo(() => {
    if (!addresses.length) return null;
    return addresses.find((a) => a?.isDefault) || addresses[0];
  }, [addresses]);

  useEffect(() => {
    if (selectedAddressId) return;
    const defId = defaultAddress?.id ?? defaultAddress?._id ?? "";
    if (defId) setSelectedAddressId(String(defId));
  }, [defaultAddress, selectedAddressId]);

  const selectedAddress = useMemo(() => {
    if (!selectedAddressId) return defaultAddress || null;
    return addresses.find((a) => String(a?.id ?? a?._id) === String(selectedAddressId)) || defaultAddress || null;
  }, [addresses, selectedAddressId, defaultAddress]);

  // ─────────────────────────────────────────────
  //  CART ACTIONS (logged-in)
  // ─────────────────────────────────────────────
  const updateQty = async (cartItemId, newQty) => {
    if (!cartItemId || !Number.isFinite(Number(newQty)) || newQty < 1) return;
    try {
      setUpdatingId(cartItemId);
      setErr("");
      // Optimistically update qty in state instantly
      setData((prev) => {
        if (!prev) return prev;
        const key = prev.CartItems ? "CartItems" : prev.cartItems ? "cartItems" : "items";
        const updated = {
          ...prev, [key]: (prev[key] || []).map((it) =>
            String(it?.id ?? it?.cartItemId ?? it?._id) === String(cartItemId)
              ? { ...it, qty: newQty, quantity: newQty }
              : it
          )
        };
        fireCartCount(updated);
        return updated;
      });
      const res = await axiosInstance.put(`/api/cart/${cartItemId}`, { qty: newQty });
      const updated = res?.data;
      if (updated && (updated.CartItems || updated.cartItems || updated.items)) {
        setData(updated);
        fireCartCount(updated);
      } else {
        await loadCart(true); // quiet refresh
      }
      await loadDeliveryCharges();
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.message || "Failed to update quantity");
      await loadCart(false); // full refresh to sync state on error
    } finally { setUpdatingId(null); }
  };

  const removeItem = async (cartItemId) => {
    if (!cartItemId) return;
    // Optimistically remove item instantly — no confirm dialog, no page reload
    setData((prev) => {
      if (!prev) return prev;
      const key = prev.CartItems ? "CartItems" : prev.cartItems ? "cartItems" : "items";
      const filtered = (prev[key] || []).filter(
        (it) => String(it?.id ?? it?.cartItemId ?? it?._id) !== String(cartItemId)
      );
      const updated = { ...prev, [key]: filtered };
      fireCartCount(updated);
      return updated;
    });
    try {
      setDeletingId(cartItemId);
      setErr("");
      await axiosInstance.delete(`/api/cart/${cartItemId}`);
      await loadDeliveryCharges();
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.message || "Failed to remove item");
      await loadCart(false); // full refresh on error
    } finally { setDeletingId(null); }
  };

  // Guest cart actions
  const removeGuestItem = (productId) => {
    guestCart.remove(productId);
  };

  const updateGuestQty = (productId, newQty) => {
    guestCart.updateQty(productId, newQty);
  };

  // ─────────────────────────────────────────────
  //  ADDRESS ACTIONS
  // ─────────────────────────────────────────────
  const startAdd = () => { setEditingId(null); setForm(emptyForm); setFormOpen(true); setAddrMsg(""); setAddrErr(""); };
  const startEdit = (addr) => {
    const id = addr?.id ?? addr?._id;
    setEditingId(id || null);
    setForm({
      label: addr?.label || "Home", pincode: addr?.pincode || "",
      house: addr?.house || "", area: addr?.area || "",
      state: addr?.state || addr?.landmark || "",
      street: addr?.street || "", village: addr?.village || "",
      mandal: addr?.mandal || "", panchayati: addr?.panchayati || "",
      district: addr?.district || "",
      receiverFirstName: addr?.receiverFirstName || "",
      receiverLastName: addr?.receiverLastName || "", receiverPhone: addr?.receiverPhone || "",
      isDefault: !!addr?.isDefault,
    });
    setFormOpen(true); setAddrMsg(""); setAddrErr("");
  };
  const cancelForm = () => { setFormOpen(false); setEditingId(null); setForm(emptyForm); };
  const onFormChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const saveAddress = async (e) => {
    e.preventDefault();
    setAddrMsg(""); setAddrErr("");
    if (!form.label || !form.pincode || !form.house || !form.area || !form.receiverFirstName || !form.receiverPhone) {
      setAddrErr("Please fill all required fields.");
      return;
    }
    try {
      setSavingAddr(true);
      const payload = { ...form, isDefault: !!form.isDefault };
      if (editingId) { await axiosInstance.put(`/api/addresses/${editingId}`, payload); setAddrMsg("Address updated ✅"); }
      else { await axiosInstance.post("/api/addresses", payload); setAddrMsg("Address added ✅"); }
      await loadAddresses();
      setAddrAlertOpen(false);
      setFormOpen(false); setEditingId(null); setForm(emptyForm);
    } catch (e2) {
      setAddrErr(e2?.response?.data?.msg || e2?.message || "Failed to save address");
    } finally { setSavingAddr(false); }
  };

  // ─────────────────────────────────────────────
  //  PAY NOW
  // ─────────────────────────────────────────────
  const walletBalance = safeNum(wallet?.balance ?? 0);
  const canPayWithWallet = walletBalance >= safeNum(grandTotal);

  const payNow = async () => {
    if (paying) return;
    if (!addresses || addresses.length === 0) {
      setErr(""); setAddrAlertOpen(true); setFormOpen(true); startAdd();
      topRef.current?.scrollIntoView({ behavior: "smooth" }); return;
    }
    if (!selectedAddressId) { setErr("Please select an address."); topRef.current?.scrollIntoView({ behavior: "smooth" }); return; }
    if (cartItems.length === 0) { setErr("Cart is empty."); topRef.current?.scrollIntoView({ behavior: "smooth" }); return; }
    if (paymentMethod === "WALLET" && !canPayWithWallet) { setErr("Insufficient wallet balance."); topRef.current?.scrollIntoView({ behavior: "smooth" }); return; }
    if (paymentMethod === "RAZORPAY" && !rzpReady) { setErr("Razorpay not loaded. Refresh and try again."); topRef.current?.scrollIntoView({ behavior: "smooth" }); return; }

    try {
      setPaying(true); setErr("");
      const orderRes = await axiosInstance.post("/api/orders", { paymentMethod, addressId: Number(selectedAddressId) });
      const createdOrder = orderRes?.data?.order || orderRes?.data || {};
      const orderId = createdOrder?.id || createdOrder?.orderId;

      if (paymentMethod === "WALLET" || paymentMethod === "COD") {
        await loadWallet();
        navigate("/PaymentResult", {
          state: {
            status: "SUCCESS", method: paymentMethod, orderId, amount: grandTotal, currency: "INR",
            message: paymentMethod === "WALLET" ? "Wallet payment successful ✅" : "Order placed (COD) ✅"
          },
        }); return;
      }

      const rzCreate = await axiosInstance.post("/api/razorpay/create-order", { orderId: Number(orderId) });
      const rz = rzCreate?.data || {};
      const razorpayOrderId = rz?.orderId || rz?.razorpay_order_id;
      const amountPaise = safeNum(rz?.amount ?? 0);
      const currency = rz?.currency || "INR";
      const keyId = rz?.keyId || rz?.key_id || rz?.key || "";
      if (!razorpayOrderId || !amountPaise || !keyId) { setErr("Razorpay order creation failed."); return; }

      const tokenNow = localStorage.getItem("token");
      const options = {
        key: keyId,
        amount: String(amountPaise),
        currency,
        name: "Sun entrepreneur",
        description: `Order #${orderId}`,
        order_id: razorpayOrderId,
        prefill: {
          name: [selectedAddress?.receiverFirstName, selectedAddress?.receiverLastName].filter(Boolean).join(" "),
          contact: selectedAddress?.receiverPhone || "",
          email: me?.email || "",
        },
        notes: { internal_order_id: String(orderId) },
        theme: { color: "#C9A84C" },
        modal: {
          backdropclose: false,
          escape: false,
          handleback: true,
          ondismiss: function () {
            setPaying(false);
            // On mobile, this is often triggered by the back button or swiping away
            console.log("Razorpay modal closed by user");
          },
        },
        handler: async function (response) {
          try {
            setPaying(true); // Keep loading state during verification
            if (tokenNow && !localStorage.getItem("token")) {
              localStorage.setItem("token", tokenNow);
            }
            
            await axiosInstance.post("/api/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            await loadCart();
            await loadWallet();
            
            navigate("/PaymentResult", {
              state: {
                status: "SUCCESS",
                method: "RAZORPAY",
                orderId,
                amount: grandTotal,
                currency: "INR",
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                message: "Razorpay payment successful ✅",
              },
            });
          } catch (e) {
            console.error("Verification Error:", e);
            navigate("/payment-result", {
              state: {
                status: "FAILED",
                method: "RAZORPAY",
                orderId,
                amount: grandTotal,
                currency: "INR",
                message: e?.response?.data?.msg || "Payment verification failed ❌",
              },
            });
          } finally {
            setPaying(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      
      // Handle initialization errors (common on non-HTTPS mobile environments)
      rzp.on('payment.failed', function (response) {
        setErr(`Payment failed: ${response.error.description}`);
        console.error("Razorpay Error:", response.error);
      });

      rzp.open();
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.message || "Payment initialization failed";
      setErr(errMsg);
      console.error("PayNow Error:", e);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    } finally {
      // Note: setPaying(false) should only happen if rzp.open() fails or immediately for non-RZP methods.
      // For RZP success, the handler takes care of it.
      if (paymentMethod !== "RAZORPAY") setPaying(false);
    }
  };

  // ─────────────────────────────────────────────
  //  GUEST CART SUBTOTAL
  // ─────────────────────────────────────────────
  const guestSubtotal = useMemo(() =>
    guestItems.reduce((s, it) => s + safeNum(it.product?.price ?? 0) * safeNum(it.qty), 0),
    [guestItems]);

  // ─────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────
  return (
    <div className="cart-page">
      <style>{cartCss}</style>

      <div className="cart-container" ref={topRef}>

        {/* ══ MERGING OVERLAY ══ */}
        {merging && (
          <div className="cart-merging">
            <div className="cart-merging-inner">
              <div className="cart-spinner" />
              <div>Syncing your cart…</div>
            </div>
          </div>
        )}

        {/* ══ TOP BAR ══ */}
        <div className="cart-topbar">
          <button className="cart-back-btn" onClick={() => navigate(-1)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Back
          </button>
          <div className="cart-topbar-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            My Cart
          </div>
          {isLoggedIn && (
            <button className="cart-refresh-btn" onClick={() => { loadCart(); loadAddresses(); loadWallet(); loadDeliveryCharges(); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
            </button>
          )}
        </div>

        {/* ══ ERROR ══ */}
        {err && <div className="cart-err">{err}</div>}

        {/* ══════════════════════════════════════════
            GUEST MODE — show localStorage cart
        ═══════════════════════════════════════════ */}
        {!isLoggedIn && (
          <div className="cart-guest-mode">
            {/* LOGIN BANNER */}
            <div className="cart-login-banner">
              <div className="cart-login-banner-left">
                <div className="cart-login-banner-icon">🔐</div>
                <div>
                  <div className="cart-login-banner-title">Login to Checkout</div>
                  <div className="cart-login-banner-sub">Your cart is saved. Login to place your order.</div>
                </div>
              </div>
              <button
                className="cart-login-btn"
                onClick={() => {
                  sessionStorage.setItem("returnTo", "/cart");
                  navigate("/login");
                }}
              >
                Login
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>

            {/* GUEST ITEMS */}
            {guestItems.length === 0 ? (
              <div className="cart-empty">
                <div className="cart-empty-icon">🛒</div>
                <div className="cart-empty-title">Your cart is empty</div>
                <div className="cart-empty-sub">Add products to get started</div>
                <button className="cart-shop-btn" onClick={() => navigate("/Order")}>Shop Now</button>
              </div>
            ) : (
              <>
                <div className="cart-section-title">
                  Cart Items
                  <span className="cart-count-badge">{guestItems.length} items</span>
                </div>

                <div className="cart-items-list">
                  {guestItems.map((it) => {
                    const p = it.product || {};
                    const imgs = normalizeImages(p?.images);
                    const img = imgUrl(imgs?.[0] || p?.image);
                    const price = safeNum(p?.price ?? 0);

                    return (
                      <div key={it.productId} className="cart-item">
                        <div className="cart-item-img-wrap">
                          <img
                            src={img || "https://via.placeholder.com/160x120?text=No+Image"}
                            alt={p?.name || "Product"}
                            onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/160x120?text=No+Image"; }}
                          />
                        </div>
                        <div className="cart-item-body">
                          <div className="cart-item-header">
                            <div className="cart-item-name">{p?.name || "Product"}</div>
                            <button
                              className="cart-item-del"
                              onClick={() => removeGuestItem(it.productId)}
                              title="Remove"
                            >🗑</button>
                          </div>
                          {p?.brand && <div className="cart-item-meta">{p.brand}</div>}
                          <div className="cart-item-price">₹ {money(price)}</div>
                          <div className="cart-item-qty-row">
                            <div className="cart-item-qty-box">
                              <button
                                className="cart-qty-btn"
                                onClick={() => updateGuestQty(it.productId, it.qty - 1)}
                                disabled={it.qty <= 1}
                              >−</button>
                              <input
                                className="cart-qty-input"
                                type="number"
                                min="1"
                                value={it.qty}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value);
                                  if (!isNaN(v) && v >= 1) updateGuestQty(it.productId, v);
                                }}
                              />
                              <button
                                className="cart-qty-btn"
                                onClick={() => updateGuestQty(it.productId, it.qty + 1)}
                              >+</button>
                            </div>
                            <div className="cart-item-line-total">₹ {money(price * it.qty)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* GUEST SUMMARY */}
                <div className="cart-guest-summary">
                  <div className="cart-summary-row">
                    <span>Items</span><b>{guestItems.length}</b>
                  </div>
                  <div className="cart-summary-row">
                    <span>Subtotal</span><b>₹ {money(guestSubtotal)}</b>
                  </div>
                  <div className="cart-summary-divider" />
                  <div className="cart-login-to-pay-note">
                    🔒 Login to see delivery charges & pay securely
                  </div>
                  <button
                    className="cart-login-pay-btn"
                    onClick={() => {
                      sessionStorage.setItem("returnTo", "/cart");
                      navigate("/login");
                    }}
                  >
                    Login &amp; Proceed to Pay
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════
            LOGGED-IN MODE — full cart + payment
        ═══════════════════════════════════════════ */}
        {isLoggedIn && (
          <div className="cart-loggedin-mode">
            {loading ? (
              <div className="cart-loading">
                <div className="cart-spinner" />
                <span>Loading cart…</span>
              </div>
            ) : (
              <div className="cart-grid">

                {/* ── COLUMN 1: ITEMS ── */}
                <div className="cart-panel">
                  <div className="cart-panel-head">
                    <div className="cart-panel-title">Cart Items</div>
                    <div className="cart-count-badge">{itemsCount} items · Qty {totalQty}</div>
                  </div>

                  {cartItems.length === 0 ? (
                    <div className="cart-empty">
                      <div className="cart-empty-icon">🛒</div>
                      <div className="cart-empty-title">Cart is empty</div>
                      <div className="cart-empty-sub">Browse products and add items</div>
                      <button className="cart-shop-btn" onClick={() => navigate("/Order")}>Shop Now</button>
                    </div>
                  ) : (
                    <div className="cart-items-list">
                      {cartItems.map((it, idx) => {
                        const p = getProduct(it);
                        const qty = getQty(it);
                        const imgs = normalizeImages(p?.images);
                        const img = imgUrl(imgs?.[0]);
                        const price = safeNum(p?.price || it?.price || 0);
                        const cartItemId = it?.id ?? it?.cartItemId ?? it?._id;
                        const isUpdating = String(updatingId) === String(cartItemId);
                        const isDeleting = String(deletingId) === String(cartItemId);

                        return (
                          <div key={cartItemId ?? idx} className="cart-item">
                            <div className="cart-item-img-wrap">
                              <img
                                src={img || "https://via.placeholder.com/160x120?text=No+Image"}
                                alt={p?.name || "Product"}
                                onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/160x120?text=No+Image"; }}
                              />
                            </div>
                            <div className="cart-item-body">
                              <div className="cart-item-header">
                                <div className="cart-item-name">{p?.name || it?.name || "—"}</div>
                                <button
                                  className="cart-item-del"
                                  onClick={() => removeItem(cartItemId)}
                                  disabled={isDeleting}
                                  title="Remove"
                                >{isDeleting ? "…" : "🗑"}</button>
                              </div>
                              {p?.brand && <div className="cart-item-meta">{p.brand} {p?.category ? `· ${p.category}` : ""}</div>}
                              <div className="cart-item-price">₹ {money(price)}</div>
                              <div className="cart-item-qty-row">
                                <div className="cart-item-qty-box">
                                  <button className="cart-qty-btn" disabled={isUpdating} onClick={() => qty > 1 ? updateQty(cartItemId, qty - 1) : removeItem(cartItemId)}>−</button>
                                  <input
                                    className="cart-qty-input"
                                    type="number"
                                    min="1"
                                    value={qty}
                                    style={{ opacity: isUpdating ? 0.6 : 1 }}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value);
                                      if (!isNaN(v) && v >= 0) {
                                         if (v === 0) return removeItem(cartItemId);
                                         updateQty(cartItemId, v);
                                      }
                                    }}
                                  />
                                  <button className="cart-qty-btn" disabled={isUpdating} onClick={() => updateQty(cartItemId, qty + 1)}>+</button>
                                </div>
                                <div className="cart-item-line-total">₹ {money(price * qty)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── COLUMN 2: ADDRESS ── */}
                <div className="cart-panel">
                  <div className="cart-panel-head">
                    <div className="cart-panel-title">Delivery Address</div>
                    <button className="cart-small-add-btn" onClick={startAdd}>+ Add</button>
                  </div>

                  {addrErr && <div className="cart-addr-err">{addrErr}</div>}
                  {addrMsg && <div className="cart-addr-ok">{addrMsg}</div>}

                  {addrLoading ? (
                    <div className="cart-info">Loading addresses…</div>
                  ) : addresses.length === 0 ? (
                    <div className="cart-info">No address found. Click "+ Add".</div>
                  ) : (
                    <>
                      <div className="cart-field">
                        <div className="cart-label">Choose Address</div>
                        <select value={selectedAddressId} onChange={(e) => setSelectedAddressId(e.target.value)} className="cart-select">
                          {addresses.map((a) => {
                            const id = a?.id ?? a?._id;
                            return (
                              <option key={id} value={String(id)}>
                                {a?.label || "Address"} · {a?.receiverFirstName || ""} · {a?.area || ""} - {a?.pincode || ""}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {selectedAddress && (
                        <div className="cart-addr-box">
                          <div className="cart-addr-box-top">
                            <div className="cart-addr-label">
                              {selectedAddress.label || "Address"}
                              {selectedAddress.isDefault && <span className="cart-default-pill">DEFAULT</span>}
                            </div>
                            <button className="cart-tiny-btn" onClick={() => startEdit(selectedAddress)}>Edit</button>
                          </div>
                          <div className="cart-addr-text">
                            {selectedAddress.receiverFirstName} {selectedAddress.receiverLastName || ""} · {selectedAddress.receiverPhone}<br />
                            {selectedAddress.house}, {selectedAddress.area}
                            {selectedAddress.state ? `, ${selectedAddress.state}` : ""}
                            {selectedAddress.street ? `, ${selectedAddress.street}` : ""}
                            {selectedAddress.village ? `, ${selectedAddress.village}` : ""}
                            {selectedAddress.mandal ? `, ${selectedAddress.mandal}` : ""}
                            {selectedAddress.panchayati ? `, ${selectedAddress.panchayati}` : ""}
                            {selectedAddress.district ? `, ${selectedAddress.district}` : ""} — {selectedAddress.pincode}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Address Form */}
                  {formOpen && (
                    <div className="cart-form-wrap">
                      <div className="cart-form-title">{editingId ? "Edit Address" : "New Address"}</div>
                      <form onSubmit={saveAddress} className="cart-addr-form">
                        {/* 1. ADDRESS INFO */}
                        <div className="cart-field cart-field-full">
                          <div className="cart-label">House / Flat *</div>
                          <input value={form.house} placeholder="House / Flat / Plot No." onChange={(e) => onFormChange("house", e.target.value)} className="cart-input" />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Street</div>
                          <input
                            value={form.street}
                            placeholder="Street / Lane"
                            onChange={(e) => {
                              onFormChange("street", e.target.value);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Area *</div>
                          <input
                            value={form.area}
                            placeholder="Area / Locality"
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                              onFormChange("area", val);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Village</div>
                          <input
                            value={form.village}
                            placeholder="Village"
                            onChange={(e) => {
                              onFormChange("village", e.target.value);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Panchayati</div>
                          <input
                            value={form.panchayati}
                            placeholder="Panchayati"
                            onChange={(e) => {
                              onFormChange("panchayati", e.target.value);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Mandal</div>
                          <input
                            value={form.mandal}
                            placeholder="Mandal"
                            onChange={(e) => {
                              onFormChange("mandal", e.target.value);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">District</div>
                          <input
                            value={form.district}
                            placeholder="District"
                            onChange={(e) => {
                              onFormChange("district", e.target.value);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">State *</div>
                          <input
                            value={form.state}
                            placeholder="State"
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^a-zA-Z ]/g, "");
                              onFormChange("state", val);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Pincode *</div>
                          <input
                            value={form.pincode}
                            maxLength={6}
                            inputMode="numeric"
                            placeholder="6-digit Pincode"
                            pattern="[0-9]{6}"
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                              onFormChange("pincode", val);
                            }}
                            className="cart-input"
                          />
                        </div>

                        {/* 2. RECEIVER INFO (Moved to Bottom) */}
                        <div className="cart-field">
                          <div className="cart-label">First Name *</div>
                          <input
                            value={form.receiverFirstName}
                            placeholder="First Name"
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^a-zA-Z ]/g, "");
                              onFormChange("receiverFirstName", val);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Last Name</div>
                          <input
                            value={form.receiverLastName}
                            placeholder="Last Name"
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^a-zA-Z ]/g, "");
                              onFormChange("receiverLastName", val);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Phone *</div>
                          <input
                            value={form.receiverPhone}
                            maxLength={10}
                            inputMode="numeric"
                            placeholder="Mobile Number"
                            pattern="[0-9]{10}"
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                              onFormChange("receiverPhone", val);
                            }}
                            className="cart-input"
                          />
                        </div>
                        <div className="cart-field">
                          <div className="cart-label">Label *</div>
                          <select value={form.label} onChange={(e) => onFormChange("label", e.target.value)} className="cart-select">
                            <option>Home</option><option>Office</option><option>Other</option>
                          </select>
                        </div>

                        <div className="cart-field cart-field-full">
                          <label className="cart-checkbox-label">
                            <input type="checkbox" checked={!!form.isDefault} onChange={(e) => onFormChange("isDefault", e.target.checked)} />
                            Make this my default address
                          </label>
                        </div>
                        <div className="cart-field cart-field-full cart-form-actions">
                          <button type="submit" className="cart-save-btn" disabled={savingAddr}>
                            {savingAddr ? "Saving…" : editingId ? "Update" : "Save"}
                          </button>
                          <button type="button" className="cart-cancel-btn" onClick={cancelForm}>Cancel</button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                {/* ── COLUMN 3: SUMMARY + PAYMENT ── */}
                <div className="cart-panel">
                  <div className="cart-panel-head">
                    <div className="cart-panel-title">Order Summary</div>
                  </div>

                  <div className="cart-summary-box">
                    {/* Products mini list */}
                    <div className="cart-products-mini">
                      <div className="cart-mini-head">Products</div>
                      {summaryProducts.length === 0 ? (
                        <div className="cart-mini-empty">No items</div>
                      ) : summaryProducts.map((p) => (
                        <div key={p.id} className="cart-mini-row">
                          <div className="cart-mini-name">
                            {p.name}
                            {p.percent > 0 && (
                              <div className="cart-mini-disc">
                                Discount: {pctText(p.percent)} (−₹ {money(p.lineDiscount)})
                              </div>
                            )}
                          </div>
                          <div className="cart-mini-qty">×{p.qty}</div>
                        </div>
                      ))}
                    </div>

                    <div className="cart-summary-row"><span>Products</span><b>{itemsCount}</b></div>
                    <div className="cart-summary-row"><span>Total Qty</span><b>{totalQty}</b></div>
                    <div className="cart-summary-row"><span>Subtotal</span><b>₹ {money(subtotal)}</b></div>
                    <div className="cart-summary-row">
                      <span>Discount ({userType || "USER"})</span>
                      <b className={discountAmount > 0 ? "cart-green" : "cart-muted"}>−₹ {money(discountAmount)}</b>
                    </div>
                    <div className="cart-summary-row"><span>After Discount</span><b>₹ {money(subtotalAfterDiscount)}</b></div>
                    <div className="cart-summary-row">
                      <span>Delivery</span>
                      <b>
                        {dcLoading ? "…" : dcErr ? <span className="cart-red">Error</span>
                          : deliveryCharge > 0 ? `₹ ${money(deliveryCharge)}`
                            : <span className="cart-green">FREE</span>}
                      </b>
                    </div>
                    <div className="cart-summary-divider" />
                    <div className="cart-summary-row cart-summary-total">
                      <span>Grand Total</span><b>₹ {money(grandTotal)}</b>
                    </div>

                    {/* Wallet */}
                    <div className="cart-wallet-box">
                      <div className="cart-wallet-head">
                        <div>Wallet Balance</div>
                        <button className="cart-tiny-btn" onClick={loadWallet} disabled={walletLoading}>
                          {walletLoading ? "…" : "↻"}
                        </button>
                      </div>
                      {walletErr ? (
                        <div className="cart-red">{walletErr}</div>
                      ) : (
                        <div className="cart-wallet-bal">₹ {money(wallet?.balance)}</div>
                      )}
                      {paymentMethod === "WALLET" && (
                        <div className={canPayWithWallet ? "cart-green" : "cart-red"} style={{ fontSize: 12, marginTop: 6, fontWeight: 800 }}>
                          {canPayWithWallet ? "✅ Sufficient balance" : "❌ Insufficient balance"}
                        </div>
                      )}
                    </div>

                    {/* Payment Method */}
                    <div className="cart-field" style={{ marginTop: 12 }}>
                      <div className="cart-label">Payment Method</div>
                      <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="cart-select">
                        <option value="RAZORPAY">Razorpay (Card / UPI / Net Banking)</option>
                        <option value="WALLET">Wallet</option>
                      </select>
                      {paymentMethod === "RAZORPAY" && (
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                          Razorpay: {rzpReady ? <b className="cart-green">Ready</b> : <b className="cart-red">Not loaded</b>}
                        </div>
                      )}
                    </div>

                    {/* Pay Button */}
                    <button
                      className="cart-pay-btn"
                      onClick={payNow}
                      disabled={
                        paying || cartItems.length === 0 || !selectedAddressId ||
                        (paymentMethod === "WALLET" && !canPayWithWallet) ||
                        (paymentMethod === "RAZORPAY" && !rzpReady)
                      }
                    >
                      {paying ? (
                        <><div className="cart-spinner cart-spinner--sm" /> Processing…</>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
                          Pay ₹ {money(grandTotal)}
                        </>
                      )}
                    </button>

                    <div className="cart-pay-note">
                      Delivery shown as <b>FREE</b> if no delivery rule applies.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ ADDRESS REQUIRED MODAL ══ */}
        {addrAlertOpen && (
          <div className="cart-modal-overlay" onClick={() => setAddrAlertOpen(false)}>
            <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cart-modal-title">Address Required</div>
              <div className="cart-modal-body">Please add a delivery address to continue payment.</div>
              <div className="cart-modal-actions">
                <button className="cart-cancel-btn" onClick={() => setAddrAlertOpen(false)}>Close</button>
                <button className="cart-save-btn" onClick={() => { setAddrAlertOpen(false); startAdd(); topRef.current?.scrollIntoView({ behavior: "smooth" }); }}>
                  + Add Address
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  CSS
// ─────────────────────────────────────────────
const cartCss = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&family=DM+Mono&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; }
  body {
    font-family: 'DM Sans', ui-sans-serif, system-ui;
    background: #f8f9fb;
    color: #1a1f2e;
    overflow-x: hidden;
  }
  input, select, button, textarea { font-family: inherit; }

  :root {
    --navy: #0F1E3D;
    --gold: #C9A84C;
    --gold-light: rgba(201,168,76,.12);
    --green: #15803d;
    --green-light: rgba(21,128,61,.10);
    --red: #b91c1c;
    --red-light: rgba(185,28,28,.08);
    --blue: #0369a1;
    --blue-light: rgba(3,105,161,.10);
    --stroke: rgba(26,31,46,.09);
    --stroke2: rgba(26,31,46,.14);
    --panel: #ffffff;
    --bg: #f8f9fb;
    --muted: rgba(26,31,46,.55);
  }

  /* ── PAGE ── */
  .cart-page { min-height: 100svh; background: var(--bg); padding-bottom: 60px; }
  .cart-container { width: min(1300px, 100%); margin: 0 auto; padding: 16px 14px; }

  /* ── TOP BAR ── */
  .cart-topbar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 18px; padding-bottom: 14px;
    border-bottom: 1px solid var(--stroke);
  }
  .cart-back-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 9px 14px; border-radius: 12px;
    border: 1px solid var(--stroke2); background: var(--panel);
    color: #1a1f2e; font-weight: 700; font-size: 13px; cursor: pointer;
    transition: background .15s;
  }
  .cart-back-btn:hover { background: #f1f3f7; }
  .cart-topbar-title {
    display: flex; align-items: center; gap: 8px;
    font-size: 20px; font-weight: 900; color: var(--navy); flex: 1;
  }
  .cart-refresh-btn {
    width: 38px; height: 38px; border-radius: 999px;
    border: 1px solid var(--stroke); background: var(--panel);
    color: #1a1f2e; cursor: pointer; display: grid; place-items: center;
  }
  .cart-refresh-btn:hover { background: #f1f3f7; }

  /* ── ERROR ── */
  .cart-err {
    padding: 12px 14px; border-radius: 12px; margin-bottom: 14px;
    background: var(--red-light); border: 1px solid rgba(185,28,28,.20);
    color: var(--red); font-weight: 700; font-size: 14px;
  }

  /* ── MERGING OVERLAY ── */
  .cart-merging {
    position: fixed; inset: 0; background: rgba(15,30,61,.55);
    display: grid; place-items: center; z-index: 999;
  }
  .cart-merging-inner {
    background: white; border-radius: 18px; padding: 28px 32px;
    display: flex; align-items: center; gap: 14px;
    font-weight: 800; font-size: 15px; color: var(--navy);
    box-shadow: 0 20px 60px rgba(0,0,0,.25);
  }

  /* ── SPINNER ── */
  .cart-spinner {
    width: 28px; height: 28px; border-radius: 999px;
    border: 3px solid rgba(201,168,76,.25);
    border-top-color: var(--gold);
    animation: spin .7s linear infinite; flex-shrink: 0;
  }
  .cart-spinner--sm { width: 16px; height: 16px; border-width: 2px; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── LOADING ── */
  .cart-loading {
    display: flex; align-items: center; gap: 12px; justify-content: center;
    padding: 60px 0; font-size: 15px; font-weight: 700; color: var(--muted);
  }

  /* ── GRID (3 columns logged in) ── */
  .cart-grid {
    display: grid; grid-template-columns: 1.15fr .95fr .8fr; gap: 14px; align-items: start;
  }
  @media (max-width: 1100px) { .cart-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 700px) { .cart-grid { grid-template-columns: 1fr; } }

  /* ── PANEL ── */
  .cart-panel {
    background: var(--panel); border-radius: 18px;
    border: 1px solid var(--stroke); padding: 16px;
    box-shadow: 0 8px 24px rgba(0,0,0,.05);
  }
  .cart-panel-head {
    display: flex; justify-content: space-between; align-items: center;
    gap: 10px; margin-bottom: 14px; flex-wrap: wrap;
  }
  .cart-panel-title { font-size: 15px; font-weight: 900; color: var(--navy); }
  .cart-count-badge {
    padding: 5px 10px; border-radius: 999px; font-size: 12px; font-weight: 800;
    background: var(--blue-light); border: 1px solid rgba(3,105,161,.18); color: var(--blue);
    white-space: nowrap;
  }

  /* ── SECTION TITLE (guest) ── */
  .cart-section-title {
    font-size: 15px; font-weight: 900; color: var(--navy);
    margin-bottom: 12px; display: flex; align-items: center; gap: 10px;
  }

  /* ── ITEMS LIST ── */
  .cart-items-list { display: grid; gap: 10px; }
  .cart-item {
    display: grid; grid-template-columns: 100px 1fr; gap: 12px;
    border-radius: 14px; border: 1px solid var(--stroke); overflow: hidden;
    background: var(--bg); transition: box-shadow .2s;
  }
  .cart-item:hover { box-shadow: 0 6px 20px rgba(0,0,0,.07); }
  .cart-item-img-wrap { background: #f1f4f8; min-height: 90px; }
  .cart-item-img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .cart-item-body { padding: 12px 12px 12px 0; }
  .cart-item-header { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
  .cart-item-name { font-size: 13px; font-weight: 800; color: #1a1f2e; line-height: 1.3; }
  .cart-item-del {
    flex-shrink: 0; padding: 6px 8px; border-radius: 10px; border: 1px solid rgba(185,28,28,.2);
    background: var(--red-light); color: var(--red); cursor: pointer; font-size: 13px;
  }
  .cart-item-del:hover { background: rgba(185,28,28,.15); }
  .cart-item-meta { font-size: 11px; color: var(--muted); margin-bottom: 6px; }
  .cart-item-price { font-size: 14px; font-weight: 900; color: #1a1f2e; margin-bottom: 8px; }
  .cart-item-qty-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .cart-item-qty-box {
    display: flex; align-items: center; border-radius: 12px; overflow: hidden;
    border: 1px solid var(--stroke2); background: var(--panel);
  }
  .cart-qty-btn {
    width: 34px; height: 32px; border: none; background: #f1f4f8;
    color: #1a1f2e; font-size: 16px; font-weight: 900; cursor: pointer;
    transition: background .15s;
  }
  .cart-qty-btn:hover:not(:disabled) { background: #e5e9f0; }
  .cart-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .cart-qty-val { width: 36px; text-align: center; font-weight: 900; font-size: 14px; }
  .cart-qty-input {
    width: 45px; height: 32px; border: none; background: transparent;
    text-align: center; font-weight: 900; font-size: 14px; color: #1a1f2e;
    outline: none; -moz-appearance: textfield;
  }
  .cart-qty-input::-webkit-outer-spin-button,
  .cart-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .cart-item-line-total { font-size: 13px; font-weight: 800; color: var(--navy); }

  /* ── EMPTY ── */
  .cart-empty { text-align: center; padding: 40px 20px; }
  .cart-empty-icon { font-size: 40px; margin-bottom: 10px; }
  .cart-empty-title { font-size: 16px; font-weight: 900; margin-bottom: 4px; }
  .cart-empty-sub { font-size: 13px; color: var(--muted); margin-bottom: 16px; }
  .cart-shop-btn {
    padding: 10px 22px; border-radius: 12px;
    background: var(--navy); color: #fff; border: none;
    font-weight: 800; cursor: pointer;
  }

  /* ── GUEST MODE ── */
  .cart-guest-mode { display: flex; flex-direction: column; gap: 16px; }

  /* LOGIN BANNER */
  .cart-login-banner {
    border-radius: 16px; padding: 16px 18px;
    background: linear-gradient(135deg, var(--navy) 0%, #1b3468 100%);
    border: 1px solid rgba(201,168,76,.30);
    display: flex; align-items: center; justify-content: space-between;
    gap: 14px; flex-wrap: wrap;
    box-shadow: 0 10px 30px rgba(15,30,61,.20);
  }
  .cart-login-banner-left { display: flex; align-items: center; gap: 14px; }
  .cart-login-banner-icon { font-size: 28px; flex-shrink: 0; }
  .cart-login-banner-title { font-size: 16px; font-weight: 900; color: #fff; margin-bottom: 3px; }
  .cart-login-banner-sub { font-size: 12px; color: rgba(255,255,255,.70); }
  .cart-login-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 11px 20px; border-radius: 12px;
    background: var(--gold); border: none; color: var(--navy);
    font-weight: 900; font-size: 14px; cursor: pointer; white-space: nowrap;
    transition: transform .15s, box-shadow .15s;
  }
  .cart-login-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(201,168,76,.35); }

  /* GUEST SUMMARY */
  .cart-guest-summary {
    background: var(--panel); border-radius: 16px;
    border: 1px solid var(--stroke); padding: 16px;
    box-shadow: 0 8px 24px rgba(0,0,0,.05);
  }
  .cart-login-to-pay-note {
    margin: 12px 0; padding: 10px 12px; border-radius: 10px;
    background: var(--gold-light); border: 1px solid rgba(201,168,76,.25);
    font-size: 13px; font-weight: 700; color: #92620f;
  }
  .cart-login-pay-btn {
    width: 100%; padding: 14px; border-radius: 14px;
    background: linear-gradient(135deg, var(--navy), #1b3468);
    border: 1px solid rgba(201,168,76,.25); color: #fff;
    font-size: 15px; font-weight: 900; cursor: pointer;
    transition: transform .15s, box-shadow .15s;
  }
  .cart-login-pay-btn:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(15,30,61,.25); }

  /* ── SUMMARY BOX ── */
  .cart-summary-box { display: flex; flex-direction: column; gap: 0; }
  .cart-products-mini {
    background: var(--bg); border-radius: 12px; padding: 12px;
    border: 1px solid var(--stroke); margin-bottom: 12px;
  }
  .cart-mini-head { font-size: 12px; font-weight: 900; color: var(--navy); margin-bottom: 8px; }
  .cart-mini-empty { font-size: 12px; color: var(--muted); }
  .cart-mini-row {
    display: flex; justify-content: space-between; gap: 8px; align-items: flex-start;
    padding: 6px 0; border-bottom: 1px dashed var(--stroke);
  }
  .cart-mini-row:last-child { border-bottom: none; }
  .cart-mini-name { font-size: 12px; font-weight: 700; line-height: 1.3; color: #1a1f2e; }
  .cart-mini-disc { font-size: 10px; color: var(--green); margin-top: 2px; }
  .cart-mini-qty { font-size: 12px; font-weight: 900; color: var(--navy); white-space: nowrap; }

  .cart-summary-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 7px 0; border-bottom: 1px dashed var(--stroke);
    font-size: 13px; color: #334155;
  }
  .cart-summary-row b { color: #1a1f2e; font-weight: 800; }
  .cart-summary-total { font-weight: 900; font-size: 15px; border-bottom: none; }
  .cart-summary-total span, .cart-summary-total b { color: var(--navy); font-size: 15px; }
  .cart-summary-divider { height: 1px; background: var(--stroke); margin: 8px 0; }

  /* ── WALLET ── */
  .cart-wallet-box {
    background: var(--bg); border-radius: 12px; padding: 12px;
    border: 1px solid var(--stroke); margin-top: 12px;
  }
  .cart-wallet-head {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 13px; font-weight: 800; color: var(--navy); margin-bottom: 6px;
  }
  .cart-wallet-bal { font-size: 22px; font-weight: 900; color: #1a1f2e; }

  /* ── PAY BUTTON ── */
  .cart-pay-btn {
    margin-top: 14px; width: 100%; height: 50px; border-radius: 14px;
    border: none; background: linear-gradient(135deg, #0f2a5e, #1b3468);
    color: #fff; font-size: 15px; font-weight: 900; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: transform .15s, box-shadow .15s; position: relative; overflow: hidden;
  }
  .cart-pay-btn::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, transparent 40%, rgba(201,168,76,.15));
  }
  .cart-pay-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(15,30,61,.30); }
  .cart-pay-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
  .cart-pay-note { font-size: 11px; color: var(--muted); margin-top: 8px; text-align: center; }

  /* ── FORM ELEMENTS ── */
  .cart-info { padding: 10px 12px; border-radius: 10px; background: var(--bg); border: 1px solid var(--stroke); font-size: 13px; color: var(--muted); }
  .cart-addr-err { padding: 10px 12px; border-radius: 10px; background: var(--red-light); border: 1px solid rgba(185,28,28,.20); color: var(--red); font-size: 13px; font-weight: 700; margin-bottom: 8px; }
  .cart-addr-ok { padding: 10px 12px; border-radius: 10px; background: var(--green-light); border: 1px solid rgba(21,128,61,.20); color: var(--green); font-size: 13px; font-weight: 700; margin-bottom: 8px; }

  .cart-addr-box { background: var(--bg); border-radius: 12px; padding: 12px; border: 1px solid var(--stroke); margin-top: 10px; }
  .cart-addr-box-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 6px; }
  .cart-addr-label { font-size: 13px; font-weight: 900; color: var(--navy); display: flex; align-items: center; gap: 8px; }
  .cart-default-pill {
    padding: 3px 8px; border-radius: 999px; font-size: 10px; font-weight: 900;
    background: var(--blue-light); border: 1px solid rgba(3,105,161,.20); color: var(--blue);
  }
  .cart-addr-text { font-size: 12px; color: #475569; line-height: 1.6; }

  .cart-field { margin-bottom: 10px; }
  .cart-field-full { grid-column: 1 / -1; }
  .cart-label { font-size: 11px; font-weight: 800; color: #64748b; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.4px; }
  .cart-input {
    width: 100%; padding: 9px 11px; border-radius: 10px;
    border: 1px solid var(--stroke2); background: var(--panel);
    color: #1a1f2e; font-size: 13px; outline: none; transition: border-color .15s, box-shadow .15s;
  }
  .cart-input:focus { border-color: var(--gold); box-shadow: 0 0 0 3px rgba(201,168,76,.12); }
  .cart-select {
    width: 100%; padding: 9px 11px; border-radius: 10px;
    border: 1px solid var(--stroke2); background: var(--panel);
    color: #1a1f2e; font-size: 13px; outline: none; cursor: pointer;
  }
  .cart-checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #475569; cursor: pointer; padding-top: 8px; }

  .cart-form-wrap { margin-top: 14px; padding-top: 14px; border-top: 1px dashed var(--stroke); }
  .cart-form-title { font-size: 14px; font-weight: 900; color: var(--navy); margin-bottom: 12px; }
  .cart-addr-form { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .cart-form-actions { display: flex; gap: 8px; }

  .cart-save-btn {
    flex: 1; padding: 10px 16px; border-radius: 10px;
    background: var(--navy); border: none; color: #fff;
    font-weight: 800; cursor: pointer; font-size: 13px;
  }
  .cart-save-btn:hover:not(:disabled) { background: #1b3468; }
  .cart-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .cart-cancel-btn {
    padding: 10px 16px; border-radius: 10px;
    border: 1px solid var(--stroke2); background: var(--panel);
    color: #1a1f2e; font-weight: 700; cursor: pointer; font-size: 13px;
  }
  .cart-small-add-btn {
    padding: 8px 12px; border-radius: 10px;
    border: 1px solid rgba(3,105,161,.20); background: var(--blue-light);
    color: var(--blue); font-weight: 800; font-size: 12px; cursor: pointer;
  }
  .cart-tiny-btn {
    padding: 6px 10px; border-radius: 8px;
    border: 1px solid var(--stroke); background: var(--panel);
    color: #1a1f2e; font-weight: 700; font-size: 12px; cursor: pointer;
  }

  /* ── MODAL ── */
  .cart-modal-overlay {
    position: fixed; inset: 0; background: rgba(15,30,61,.45);
    display: grid; place-items: center; z-index: 200; padding: 12px;
  }
  .cart-modal {
    width: min(400px, 100%); border-radius: 18px; background: var(--panel);
    border: 1px solid var(--stroke); box-shadow: 0 20px 60px rgba(0,0,0,.22); padding: 20px;
  }
  .cart-modal-title { font-size: 16px; font-weight: 900; color: var(--navy); margin-bottom: 8px; }
  .cart-modal-body { font-size: 13px; color: #475569; line-height: 1.5; }
  .cart-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; flex-wrap: wrap; }

  .cart-loader-small {
    width: 14px; height: 14px; border: 2px solid rgba(26,31,46,0.1);
    border-top-color: var(--navy); border-radius: 50%;
    animation: cartSpin 0.6s linear infinite; display: inline-block;
  }
  @keyframes cartSpin { to { transform: rotate(360deg); } }

  /* ── UTILITIES ── */
  .cart-green { color: var(--green) !important; }
  .cart-red { color: var(--red) !important; }
  .cart-muted { color: var(--muted) !important; }

  /* ── MOBILE ── */
  @media (max-width: 480px) {
    .cart-container { padding: 12px 10px; }
    .cart-item { grid-template-columns: 80px 1fr; }
    .cart-login-banner { flex-direction: column; align-items: flex-start; }
  }
`;